import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type {
  Occupancy,
  Override,
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  DAY_OVERRIDE_MODAL_TAB_IDS,
  type DayOverrideModalTabIds,
  OVERRIDE_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  RateGroupContextFields,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import {
  calculateTotalRoomsForSale,
  getOverrideVariant,
} from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import MinLosRestrictionState, {
  type SendRestrictionConfig,
} from 'lumen/utils/pricing-assistant/min-los-restriction-state';
import {
  hasRestrictionActionChanged,
  mergeRestrictionActionsByContext,
} from 'lumen/utils/pricing-assistant/restriction-action-utils';
import { formatOverbookingValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './day-override-modal.module.css';
import OverbookingPanel from './overbooking-panel';
import type { RateGroupRow } from './rate-groups-accordion';
import Sidebar, { type SidebarItem } from './sidebar';
import StayRestrictionsPanel from './stay-restrictions-panel';

interface HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature {
  Args: {
    onCloseModal: () => void;
    period: Period;
  };
  Blocks: {
    default: [];
  };
}

interface RateGroupMinLosEntry {
  rateGroup: RateGroup;
  state: MinLosRestrictionState;
}

export default class DayOverrideModal extends Component<HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTabId: DayOverrideModalTabIds =
    DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;

  @tracked overbookingAutomationOnUserEdit?: boolean;
  @tracked overbookingTouched = false;
  @tracked overbookingUserEdit?: number | null;

  /* Created once per modal open (constructor): the instance snapshots
     initiallyHadOverride there, so it must never be rebuilt by a getter. */
  readonly hotelMinLosState: MinLosRestrictionState;
  /* Snapshot at modal open — read live it would flip after the delete + re-request,
     breaking overbookingOverrideRemoved mid-send. */
  initiallyHadOverbookingOverride: boolean = false;
  /* One entry per configured rate group, created once per modal open like
     hotelMinLosState: the set of groups and each instance's initiallyHadOverride
     snapshot are fixed at open; a meta re-fetch mid-modal changes nothing until
     reopen. */
  readonly rateGroupMinLosEntries: RateGroupMinLosEntry[];
  /* Built once with the entries. PrismTable treats a new row object as a new
     row, so rows rebuilt on every edit would re-render and the MinLOS input
     would lose focus on each keystroke. */
  readonly rateGroupRows: RateGroupRow[];

  private get configuredRateGroups(): RateGroup[] {
    if (!this.hasDeepDiscountAutopilotToggle) {
      return [];
    }

    return (
      this.pricingAssistantDataRequestService
        .requestPricingAssistantSubscriptionMetaTask.lastSuccessful?.value
        ?.configuredRateGroups ?? []
    );
  }

  get formattedOverbookingValue(): number | string {
    return formatOverbookingValue({
      intl: this.intl,
      value: this.args.period.effectiveOverbooking,
    });
  }

  private get hasDeepDiscountAutopilotToggle(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PRICING_MANAGER_DEEP_DISCOUNT_AUTOPILOT,
    );
  }

  get hasStayRestrictionsOverride(): boolean {
    return this.minLosStates.some((state) => state.hasOverride);
  }

  get hotelLevelRestriction(): Restriction | undefined {
    return this.args.period.hotelLevelRestriction;
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.hotelLevelRestrictionRecommendation;
  }

  get isOverbooked(): boolean {
    return (
      typeof this.physicalRoomsAvailable === 'number' &&
      this.physicalRoomsAvailable < 0
    );
  }

  get isOverbookingLoading(): boolean {
    return this.isAccordionLoading(
      this.args.period.hasOverbookingActionInProgress,
      this.overbookingOverrideRemoved,
      this.overbookingWillSend,
    );
  }

  /* The pre-push window shared across accordions: the override delete and the
  subsequent restrictions/recommendations re-request. Not attributable to a single
  context, so each accordion gates it on its own `overrideRemoved` getter. */
  get isPreparingSend(): boolean {
    return (
      this.pricingAssistantDataRequestService.deleteOverridesTask.isRunning ||
      this.pricingAssistantDataRequestService.restrictionsRequestTask
        .isRunning ||
      this.pricingAssistantDataRequestService
        .restrictionsRecommendationsRequestTask.isRunning
    );
  }

  /* True while the restriction-action push POST is in flight, before the action record
  lands in the store and starts driving the `has*ActionInProgress` getters. */
  get isPushingRestrictionAction(): boolean {
    return this.restrictionsDataRequestService.createRestrictionActionTask
      .isRunning;
  }

  get isSending(): boolean {
    const isActionInProgress =
      this.args.period.hasOverbookingActionInProgress ||
      this.minLosStates.some((state) => state.hasActionInProgress);

    return (
      isActionInProgress ||
      this.isPushingRestrictionAction ||
      this.isPreparingSend
    );
  }

  /* Every MinLOS restriction context handled by the modal: hotel level plus one
     instance per configured rate group (see rateGroupMinLosEntries). */
  get minLosStates(): MinLosRestrictionState[] {
    return [
      this.hotelMinLosState,
      ...this.rateGroupMinLosEntries.map((entry) => entry.state),
    ];
  }

  get modalTitle(): string {
    const carate = this.args.period.carates?.[0];
    const day = carate?.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    return `${this.intl.t('general.edit')} ${day}, ${this.intl.t('pricing_assistant.all_rooms')}`;
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  get overbooking(): number | null {
    return this.resolveUserEdit(
      this.overbookingUserEdit,
      this.args.period.effectiveOverbookingWithOverride,
    );
  }

  get overbookingAutomationOn(): boolean {
    return this.resolveUserEdit(
      this.overbookingAutomationOnUserEdit,
      this.args.period.hotelLevelRestriction?.overbookingRestrictionValueObject
        ?.autopilotEnabled ?? false,
    );
  }

  /* True when the overbooking accordion's sendable state differs from what is in
     place, i.e. the user actually changed it. */
  get overbookingChanged(): boolean {
    return hasRestrictionActionChanged(
      {
        automationOn: this.overbookingAutomationOn,
        value: this.overbooking,
      },
      {
        automationOn:
          this.hotelLevelRestriction?.overbookingRestrictionValueObject
            ?.autopilotEnabled ?? false,
        value: this.args.period.effectiveOverbookingWithOverride,
      },
    );
  }

  /* Returns `undefined` (not `null` like the MinLOS error getters) because this
     value is passed straight to PrismNumber's `@errorMessage` prop, which is typed
     `string | undefined`. The MinLOS getters instead feed a boolean `@hasErrors` and
     render their own text, so they follow the `minLosErrorMessage` util's null convention. */
  get overbookingErrorMessage(): string | undefined {
    if (!this.overbookingTouched || this.overbookingAutomationOn) {
      return undefined;
    }

    return this.overbooking === null
      ? this.intl.t('general.required')
      : undefined;
  }

  /* True when the user has toggled automation ON from an initial OFF state,
     meaning the existing override will be deleted on Send. */
  get overbookingOverrideRemoved(): boolean {
    return this.overbookingAutomationOn && this.initiallyHadOverbookingOverride;
  }

  get overbookingWillSend(): boolean {
    return this.willSend(
      this.overbookingChanged,
      this.overbookingAutomationOn,
      this.overbookingOverrideRemoved,
    );
  }

  get physicalRoomsAvailable(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking.physical_rooms_available_tooltip',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking.physical_rooms_available_tooltip',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get rateGroupOverrideCount(): number {
    return this.rateGroupMinLosEntries.filter(
      (entry) => entry.state.hasOverride,
    ).length;
  }

  get roomsForSaleValue(): string | number {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking ?? 0, // Treat empty overbooking (null) as 0 for calculation purposes
      );
    }

    return this.intl.t('general.missing');
  }

  get sendButtonLabel(): string {
    const label = this.intl.t('pricing_assistant.label.push');
    const count = this.getPendingConfigs().length;
    return count > 0 ? `${label} (${count})` : label;
  }

  get showOverbookingException(): boolean {
    return this.args.period.shouldShowOverbookingException;
  }

  get showOverbookingOverride(): boolean {
    return (
      this.args.period.hasOverbookingOverride && !this.showOverbookingException
    );
  }

  get showOverbookingTab(): boolean {
    return this.access.hasPricingAssistantOverbookingRestrictionsFeature;
  }

  get showRateGroupsCard(): boolean {
    return this.hasDeepDiscountAutopilotToggle;
  }

  /* Empty state when no rate group has rate codes mapped (including no rate
   groups at all), or the page-load meta request failed */
  get showRateGroupsEmptyState(): boolean {
    return this.rateGroupMinLosEntries.length === 0;
  }

  get showStayRestrictionsTab(): boolean {
    return this.access.hasPricingAssistantMinLosRestrictionsFeature;
  }

  get sidebarItems(): SidebarItem[] {
    const items: SidebarItem[] = [];

    if (this.showOverbookingTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING,
        hasException: this.showOverbookingException,
        hasOverride: this.showOverbookingOverride,
        label: this.intl.t('pricing_manager.overbooking.sidebar_label'),
        overrideVariant: getOverrideVariant(OVERRIDE_TYPES.OVERBOOKING),
      });
    }

    if (this.showStayRestrictionsTab) {
      items.push({
        id: DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS,
        hasException: this.args.period.shouldShowStayRestrictionException,
        hasOverride: this.hasStayRestrictionsOverride,
        label: this.intl.t('pricing_manager.restrictions.sidebar_label'),
        overrideVariant: getOverrideVariant(OVERRIDE_TYPES.MIN_LOS),
      });
    }

    return items;
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableModalsDayOverrideModalDayOverrideModalSignature['Args'],
  ) {
    super(owner, args);
    // COA-3693 TODO: remove once COA-3687 is resolved.
    // BE still sends the failed override value when a push fails, showing an exception instead.
    // Treat the override as absent so overbookingOverrideRemoved stays correct if the exception clears before send.
    if (this.showOverbookingException) {
      this.initiallyHadOverbookingOverride = false;
    } else {
      this.initiallyHadOverbookingOverride =
        this.args.period.hasOverbookingOverride;
    }
    this.hotelMinLosState = this.createMinLosState({
      context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
    });
    this.rateGroupMinLosEntries = this.configuredRateGroups.map(
      (rateGroup) => ({
        rateGroup,
        state: this.createMinLosState({
          context: RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL,
          rateGroupId: rateGroup.rateGroupId,
        }),
      }),
    );
    this.rateGroupRows = this.rateGroupMinLosEntries.map(
      ({ rateGroup, state }) => ({
        name: rateGroup.name,
        rateGroupId: rateGroup.rateGroupId,
        state,
      }),
    );
    this.activeTabId =
      this.sidebarItems[0]?.id ?? DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING;
  }

  private createMinLosState(
    contextFields: RateGroupContextFields,
  ): MinLosRestrictionState {
    return new MinLosRestrictionState({
      contextFields,
      getPeriod: (): Period => this.args.period,
      intl: this.intl,
      isPreparingSend: (): boolean => this.isPreparingSend,
      isPushingRestrictionAction: (): boolean =>
        this.isPushingRestrictionAction,
      shouldShowStayRestrictionException: (): boolean =>
        this.args.period.shouldShowStayRestrictionException,
    });
  }

  private getOverbookingRestrictionValues(): RestrictionActionValue[] | null {
    if (this.overbookingOverrideRemoved) {
      const recommendationValue =
        this.hotelLevelRestrictionRecommendation?.overbookingValue;
      return recommendationValue != null
        ? [{ type: RESTRICTION_TYPES.OVERBOOKING, value: recommendationValue }]
        : null;
    }

    if (this.overbooking == null) {
      return null;
    }

    return [{ type: RESTRICTION_TYPES.OVERBOOKING, value: this.overbooking }];
  }

  private getPendingConfigs(): SendRestrictionConfig[] {
    const configs: SendRestrictionConfig[] = [];

    if (this.showOverbookingTab && this.overbookingWillSend) {
      configs.push({
        context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
        existingOverride: this.hotelLevelRestriction?.overbookingOverride,
        restriction: this.hotelLevelRestriction,
        restrictionRecommendation: this.hotelLevelRestrictionRecommendation,
        overrideRemoved: this.overbookingOverrideRemoved,
        overrideValue: this.overbooking,
        overrideType: OVERRIDE_TYPES.OVERBOOKING,
        getRestrictionValues: () => this.getOverbookingRestrictionValues(),
        hasError: () => this.overbookingErrorMessage !== undefined,
        markTouched: () => {
          this.overbookingTouched = true;
        },
      });
    }

    if (this.showStayRestrictionsTab) {
      for (const state of this.minLosStates) {
        if (state.willSend) {
          configs.push(state.toSendConfig());
        }
      }
    }

    return configs;
  }

  /* The three checks map to the three phases a Send goes through, each gated on the
     accordion's own signals so a sibling's action never drives it:
     1. `overrideRemoved && isPreparingSend` — deleting the override (automation
        checkbox ticked) and the shared restrictions/recommendations re-request.
     2. `willSend && isPushingRestrictionAction` — the createRestrictionAction POST is
        in flight; `hasActionInProgress` is not active yet since it relies on the
        restriction-action record landing in the store.
     3. `hasActionInProgress` — the restriction-action has landed and its pollers have
        started. */
  private isAccordionLoading(
    hasActionInProgress: boolean,
    overrideRemoved: boolean,
    willSend: boolean,
  ): boolean {
    return (
      (overrideRemoved && this.isPreparingSend) ||
      (willSend && this.isPushingRestrictionAction) ||
      hasActionInProgress
    );
  }

  /* Returns the user's edit when present, otherwise the value from the API.
    `undefined` means "not edited"; a `null`/`false` edit is a real value the user set, so
     we test against `undefined` rather than falling back with `??`. */
  private resolveUserEdit<T>(userEdit: T | undefined, liveValue: T): T {
    return userEdit === undefined ? liveValue : userEdit;
  }

  private async sendAllPendingOverrides(
    configs: SendRestrictionConfig[],
  ): Promise<'success' | 'error' | null> {
    const formattedDate = this.args.period.fromDate.format('YYYY-MM-DD');
    const hotelId = this.hotelAndMarketService.hotel.id!;
    const hasRemovals = configs.some((c) => c.overrideRemoved);

    const overrideIdsToDelete = configs
      .filter((c) => c.overrideRemoved && c.existingOverride)
      .map((c) => c.existingOverride!.id);

    if (overrideIdsToDelete.length > 0) {
      const result =
        await this.pricingAssistantDataRequestService.deleteOverridesTask.perform(
          overrideIdsToDelete,
        );
      if (!result?.isSuccess) {
        return 'error';
      }
    }

    if (hasRemovals) {
      /* EmberData keeps a previously-loaded record when the re-request response omits it,
         so a restriction/recommendation that disappears after a removal would linger.
         Unload the removed contexts' records so the re-request rebuilds them cleanly.
         Untouched contexts are always returned, so leaving them loaded strands nothing and
         keeps the other accordion from flipping its automation control mid-re-request. */
      const recordsToUnload = new Set(
        configs
          .filter((c) => c.overrideRemoved)
          .flatMap((c) => [c.restriction, c.restrictionRecommendation])
          .filter((r) => r != null),
      );

      recordsToUnload.forEach((r) => this.store.unloadRecord(r));

      await Promise.all([
        this.pricingAssistantDataRequestService.restrictionsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
        this.pricingAssistantDataRequestService.restrictionsRecommendationsRequestTask.perform(
          hotelId,
          formattedDate,
          formattedDate,
        ),
      ]);
    }

    const overrides: RestrictionOverridePayload[] = [];
    for (const config of configs) {
      const shouldSendOverride = !config.overrideRemoved;
      if (shouldSendOverride && config.overrideValue !== null) {
        overrides.push({
          subscription_id: parseInt(hotelId, 10),
          from_date: formattedDate,
          value: config.overrideValue,
          type: config.overrideType,
          context: config.context,
          rate_group_id: config.rateGroupId ?? null,
        });
      }
    }

    this.restrictionsDataRequestService.overrideCallbackOnPushFinished = async (
      day: string,
      subscriptionId: string,
    ): Promise<void> => {
      this.store
        .peekAll<Override>('override')
        .filter((o: Override) => o.fromDate.format('YYYY-MM-DD') === day)
        .forEach((o: Override) => this.store.unloadRecord(o));

      await this.pricingAssistantDataRequestService.requestOverridesTask.perform(
        subscriptionId,
        day,
        day,
      );
    };

    /* Construct one restriction_action_config per context by merging the config
       objects that belong to the same context. */
    const restrictionActionData = mergeRestrictionActionsByContext(
      configs,
      formattedDate,
    );

    /* Empty when no config yielded a value to send: a removal whose re-request returned no
       recommendation, or a send with no value entered. */
    if (restrictionActionData.length === 0) {
      return hasRemovals ? 'success' : null;
    }

    try {
      await this.restrictionsDataRequestService.sendRestriction(
        restrictionActionData,
        overrides,
      );

      return 'success';
    } catch {
      return 'error';
    }
  }

  /* True when the overbooking change will be sent on the next push, covering both a
     removal and a value change with automation off. */
  private willSend(
    changed: boolean,
    automationOn: boolean,
    overrideRemoved: boolean,
  ): boolean {
    return changed && (!automationOn || overrideRemoved);
  }

  @action
  onCloseModal(): void {
    this.args.onCloseModal();
  }

  @action
  onOverbookingBlur(): void {
    this.overbookingTouched = true;
  }

  @action
  async onSend(): Promise<void> {
    if (this.isSending) {
      return;
    }

    const configs = this.getPendingConfigs();

    if (configs.length === 0) {
      this.onCloseModal();
      return;
    }

    /* Set pending configs' input fields touched so blocking errors are shown. */
    configs.forEach((config) => config.markTouched());
    if (configs.some((config) => config.hasError())) {
      return;
    }

    for (const config of configs) {
      this.usageAnalytics.track(
        config.overrideRemoved
          ? 'Autopilot Override Disabled'
          : 'Autopilot Override Enabled',
        {
          eventTrigger: 'day-override-modal',
          product: 'pricing-manager',
          overrideType:
            config.overrideType === OVERRIDE_TYPES.MIN_LOS
              ? 'min_los'
              : 'overbooking',
        },
      );
    }

    const outcome = await this.sendAllPendingOverrides(configs);

    if (outcome === 'success') {
      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } else if (outcome === 'error') {
      this.toast.error(this.intl.t('general.error_short'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    }

    this.onCloseModal();
  }

  @action
  selectTab(id: DayOverrideModalTabIds): void {
    this.activeTabId = id;
  }

  @action
  toggleOverbookingAutomation(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.overbookingAutomationOnUserEdit = checked;

    if (checked && this.initiallyHadOverbookingOverride) {
      // Turning automation ON when override exists — show recommendation value
      this.overbookingUserEdit = this.args.period.effectiveOverbooking;
    } else {
      // Toggling off, or on with no override — drop any user edit
      this.overbookingUserEdit = undefined;
      this.overbookingTouched = false;
    }
  }

  @action
  updateOverbooking(value: number | null): void {
    this.overbookingUserEdit = value;
  }

  <template>
    <PrismModal
      @onClose={{this.onCloseModal}}
      @title={{this.modalTitle}}
      class={{styles.modal}}
    >
      <:default>
        <div class={{styles.body}}>
          <div class="text-300--regular">
            <Sidebar
              @activeTabId={{this.activeTabId}}
              @items={{this.sidebarItems}}
              @onSelect={{this.selectTab}}
            />
          </div>
          <div class={{styles.content}} tabindex="0">
            {{#if (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.OVERBOOKING)}}
              <OverbookingPanel
                @automationOn={{this.overbookingAutomationOn}}
                @effectiveOverbookingWithOverride={{@period.effectiveOverbookingWithOverride}}
                @errorMessage={{this.overbookingErrorMessage}}
                @failedOverbookingValue={{@period.failedOverbookingValue}}
                @formattedOverbooking={{this.formattedOverbookingValue}}
                @hasOverbookingOverride={{this.showOverbookingOverride}}
                @isLoading={{this.isOverbookingLoading}}
                @isOverbooked={{this.isOverbooked}}
                @isTouched={{this.overbookingTouched}}
                @onOverbookingBlur={{this.onOverbookingBlur}}
                @onToggleAutomation={{this.toggleOverbookingAutomation}}
                @onUpdateOverbooking={{this.updateOverbooking}}
                @overbooking={{this.overbooking}}
                @physicalRoomsAvailable={{this.physicalRoomsAvailable}}
                @physicalRoomsAvailableLabel={{this.physicalRoomsAvailableLabel}}
                @roomsForSaleValue={{this.roomsForSaleValue}}
                @showOverbookingException={{this.showOverbookingException}}
              />
            {{else if
              (eq this.activeTabId DAY_OVERRIDE_MODAL_TAB_IDS.STAY_RESTRICTIONS)
            }}
              <StayRestrictionsPanel
                @hotelState={{this.hotelMinLosState.accordionState}}
                @rateGroupOverrideCount={{this.rateGroupOverrideCount}}
                @rateGroupRows={{this.rateGroupRows}}
                @showRateGroups={{this.showRateGroupsCard}}
                @showRateGroupsEmptyState={{this.showRateGroupsEmptyState}}
              />
            {{/if}}
          </div>
        </div>
      </:default>
      <:footer>
        <div
          class="mx-500 flex h-full flex-row items-center justify-end gap-200"
        >
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.onCloseModal}}
          />
          <PrismButton
            @buttonType="primary"
            @iconBefore="send"
            @isLoading={{this.isSending}}
            @label={{this.sendButtonLabel}}
            @onClick={{this.onSend}}
            data-test-id="day-override-send-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
